import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MobileStepContainerProps {
  children: ReactNode;
  className?: string;
  fullWidth?: boolean;
}

/**
 * Container para passos de onboarding em dispositivos móveis.
 * Fornece estrutura e estilos adequados para melhorar a experiência em telas pequenas.
 */
export const MobileStepContainer: React.FC<MobileStepContainerProps> = ({
  children,
  className,
  fullWidth = false
}) => {
  return (
    <div
      className={cn(
        "mobile-step-container",
        fullWidth ? "w-full" : "w-full mx-auto",
        className
      )}
    >
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
};

/**
 * Seção para agrupar elementos relacionados dentro de um passo.
 */
export const MobileStepSection: React.FC<{ 
  children: ReactNode; 
  title?: string;
  description?: string;
  className?: string;
}> = ({ 
  children, 
  title, 
  description,
  className 
}) => {
  return (
    <div className={cn("mobile-step-section card p-3 bg-white rounded-lg border border-gray-200", className)}>
      {title && <h2 className="text-base font-medium mb-2">{title}</h2>}
      {description && <p className="text-xs text-gray-600 mb-3">{description}</p>}
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

/**
 * Item de formulário com layout otimizado para mobile.
 */
export const MobileFormItem: React.FC<{
  children: ReactNode;
  label?: string;
  description?: string;
  error?: string;
  className?: string;
}> = ({ 
  children, 
  label, 
  description, 
  error,
  className
}) => {
  return (
    <div className={cn("form-group", className)}>
      {label && <label className="block text-sm font-medium mb-1">{label}</label>}
      {description && <p className="text-xs text-gray-500 mb-1">{description}</p>}
      <div className="fullwidth-input">
        {children}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

/**
 * Botão de ação grande e otimizado para toque em mobile.
 */
export const MobileActionButton: React.FC<{
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}> = ({
  children,
  onClick,
  className,
  disabled = false,
  type = 'button'
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "interactive-area w-full rounded-md bg-primary text-white font-medium py-2.5 px-3",
        "transition-element hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {children}
    </button>
  );
}; 